import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import ConfirmModal from '../components/ConfirmModal';
import PremiumSelect from '../components/PremiumSelect';
import { Cycle, AlertState } from '../types';
import './Cycles.css';

interface CycleFormData {
  name: string;
  startDate: string;
  endDate: string;
  deadlineDate: string;
  active: boolean;
}

interface ConfirmDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => Promise<void>;
}

const createEmptyForm = (): CycleFormData => ({
  name: '',
  startDate: '',
  endDate: '',
  deadlineDate: '',
  active: true,
});

const normalizeDateInput = (value: string): string => value.slice(0, 10);

const formatDate = (value: string): string => {
  const normalized = normalizeDateInput(value);
  const [year, month, day] = normalized.split('-');
  return `${day}/${month}/${year}`;
};

const getCycleYear = (cycle: Cycle): string => normalizeDateInput(cycle.startDate).slice(0, 4);

const getCyclePeriodKey = (cycle: Cycle): string => normalizeDateInput(cycle.startDate).slice(0, 7);

const getCyclePeriodLabel = (periodKey: string): string => {
  const [year, month] = periodKey.split('-');
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, 1));
  const label = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(date);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const Cycles: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingCycleId, setEditingCycleId] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('all');
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState<boolean>(false);
  const [formData, setFormData] = useState<CycleFormData>(createEmptyForm());
  const formSectionRef = useRef<HTMLElement | null>(null);
  const shouldScrollToFormRef = useRef<boolean>(false);

  const scrollToSection = useCallback((section: HTMLElement | null, extraOffset = 48): void => {
    if (!section) return;

    const run = (): void => {
      const header = document.querySelector('.app-header');
      const headerHeight = header?.getBoundingClientRect().height ?? 96;
      const top =
        section.getBoundingClientRect().top + window.scrollY - headerHeight - extraOffset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    };

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });
  }, []);

  const loadCycles = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Cycle[]>('/cycles');
      setCycles(response.data);
    } catch (error) {
      console.error('Erro ao carregar ciclos:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCycles();
  }, [loadCycles]);

  useEffect(() => {
    if (!showForm || !shouldScrollToFormRef.current) {
      return;
    }

    shouldScrollToFormRef.current = false;
    scrollToSection(formSectionRef.current, 48);
  }, [editingCycleId, scrollToSection, showForm]);

  const resetForm = useCallback((): void => {
    setFormData(createEmptyForm());
    setEditingCycleId(null);
  }, []);

  const openCreateForm = (): void => {
    if (showForm && !editingCycleId) {
      setShowForm(false);
      resetForm();
      return;
    }

    resetForm();
    shouldScrollToFormRef.current = true;
    setFormData((prev) => ({
      ...prev,
      active: !cycles.some((c) => c.active),
    }));
    setShowForm(true);
  };

  const openEditForm = (cycle: Cycle): void => {
    shouldScrollToFormRef.current = true;
    setEditingCycleId(cycle.id);
    setFormData({
      name: cycle.name,
      startDate: normalizeDateInput(cycle.startDate),
      endDate: normalizeDateInput(cycle.endDate),
      deadlineDate: normalizeDateInput(cycle.deadlineDate),
      active: cycle.active,
    });
    setShowForm(true);
  };

  const closeForm = (): void => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const currentEditingCycleId = editingCycleId;

    try {
      if (currentEditingCycleId) {
        await api.patch(`/cycles/${currentEditingCycleId}`, formData);
      } else {
        await api.post('/cycles', formData);
      }

      closeForm();
      await loadCycles();
      setAlert({
        isOpen: true,
        message: currentEditingCycleId ? 'Período atualizado com sucesso!' : 'Período criado com sucesso!',
        type: 'success',
      });
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error.response?.data?.message ||
          (currentEditingCycleId ? 'Erro ao atualizar período' : 'Erro ao criar período'),
        type: 'error',
      });
    }
  };

  const handleDelete = async (cycle: Cycle): Promise<void> => {
    setConfirmDialog({
      isOpen: true,
      title: 'Excluir período',
      message: `Deseja realmente excluir o período ${cycle.name}?`,
      onConfirm: async () => {
        try {
          await api.delete(`/cycles/${cycle.id}`);
          await loadCycles();
          setAlert({
            isOpen: true,
            message: 'Período excluído com sucesso!',
            type: 'success',
          });
        } catch (error: any) {
          setAlert({
            isOpen: true,
            message: error.response?.data?.message || 'Erro ao excluir período',
            type: 'error',
          });
        }
      },
    });
  };

  const closeConfirmDialog = (): void => {
    if (isConfirmingDelete) return;
    setConfirmDialog(null);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!confirmDialog) return;
    setIsConfirmingDelete(true);
    try {
      await confirmDialog.onConfirm();
      setConfirmDialog(null);
    } finally {
      setIsConfirmingDelete(false);
    }
  };

  const sortedCycles = useMemo(
    () =>
      [...cycles].sort(
        (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
      ),
    [cycles]
  );

  const activeCycle = useMemo(
    () => sortedCycles.find((cycle) => cycle.active) || null,
    [sortedCycles]
  );

  const availableYears = useMemo(
    () =>
      Array.from(new Set(sortedCycles.map((cycle) => getCycleYear(cycle)))).sort(
        (a, b) => Number(b) - Number(a)
      ),
    [sortedCycles]
  );

  const availablePeriods = useMemo(() => {
    const sourceCycles =
      selectedYear === 'all'
        ? sortedCycles
        : sortedCycles.filter((cycle) => getCycleYear(cycle) === selectedYear);

    const periodMap = new Map<string, string>();
    sourceCycles.forEach((cycle) => {
      const key = getCyclePeriodKey(cycle);
      if (!periodMap.has(key)) {
        periodMap.set(key, getCyclePeriodLabel(key));
      }
    });

    return Array.from(periodMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => b.value.localeCompare(a.value));
  }, [selectedYear, sortedCycles]);

  useEffect(() => {
    if (selectedPeriod !== 'all' && !availablePeriods.some((period) => period.value === selectedPeriod)) {
      setSelectedPeriod('all');
    }
  }, [availablePeriods, selectedPeriod]);

  const filteredCycles = useMemo(
    () =>
      sortedCycles.filter((cycle) => {
        const matchesYear = selectedYear === 'all' || getCycleYear(cycle) === selectedYear;
        const matchesPeriod = selectedPeriod === 'all' || getCyclePeriodKey(cycle) === selectedPeriod;
        return matchesYear && matchesPeriod;
      }),
    [selectedPeriod, selectedYear, sortedCycles]
  );

  const cycleToolbarYearOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos os anos' },
      ...availableYears.map((y) => ({ value: y, label: y })),
    ],
    [availableYears],
  );

  const cycleToolbarPeriodOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos os períodos' },
      ...availablePeriods.map((p) => ({ value: p.value, label: p.label })),
    ],
    [availablePeriods],
  );

  const cycleActiveFieldOptions = useMemo(
    () => [
      { value: 'true', label: 'Sim — período vigente' },
      { value: 'false', label: 'Não — período encerrado' },
    ],
    [],
  );

  const stats = useMemo(() => {
    const total = filteredCycles.length;
    const activeCount = filteredCycles.filter((cycle) => cycle.active).length;
    const closed = filteredCycles.filter((cycle) => !cycle.active).length;
    return { total, activeCount, closed };
  }, [filteredCycles]);

  const getInitials = (name: string): string =>
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'P';

  const deadlineTone = (cycle: Cycle): 'is-ok' | 'is-soon' | 'is-past' => {
    const end = new Date(`${normalizeDateInput(cycle.deadlineDate)}T23:59:59`).getTime();
    const now = Date.now();
    if (end < now) return 'is-past';
    const daysLeft = Math.ceil((end - now) / (86400 * 1000));
    if (daysLeft <= 7) return 'is-soon';
    return 'is-ok';
  };

  const canManage = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr';
  const canDelete = user?.role === 'admin';
  const hasFilters = selectedYear !== 'all' || selectedPeriod !== 'all';

  if (loading) {
    return (
      <div className="cycles-state">
        <div className="cycles-state-card">
          <span className="cycles-badge">Carregando</span>
          <h1>Preparando períodos</h1>
          <p>Buscando ciclos mensais, prazos e status da operação.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cycles-page">
      <section className="cycles-hero">
        <div className="cycles-hero-copy">
          <span className="cycles-badge">Planejamento</span>
          <h1>Períodos de aferição</h1>
          <p>
            Organize os meses de avaliação, acompanhe o prazo de fechamento e mantenha um período
            ativo visível para toda a operação.
          </p>
        </div>

        <div className="cycles-hero-side">
          <div className="cycles-hero-card">
            {activeCycle ? (
              <>
                <span className="cycles-hero-card-label">Período em vigor</span>
                <strong>{activeCycle.name}</strong>
                <p>
                  Fechamento previsto para{' '}
                  <span className="cycles-hero-emphasis">{formatDate(activeCycle.deadlineDate)}</span>
                </p>
                {user?.role === 'manager' && (
                  <div className="cycles-hero-actions">
                    <Link to="/financeiro" className="cycles-action-button">
                      Novo lançamento
                    </Link>
                  </div>
                )}
              </>
            ) : (
              <>
                <span className="cycles-hero-card-label">Status</span>
                <strong>Nenhum período ativo</strong>
                <p>Ative um período para liberar lançamentos e aferições neste mês.</p>
              </>
            )}
          </div>
          {canManage && (
            <button
              type="button"
              onClick={openCreateForm}
              className={`cycles-action-button cycles-hero-cta${showForm ? ' is-secondary' : ''}`}
            >
              {showForm && !editingCycleId ? 'Fechar formulário' : 'Novo período'}
            </button>
          )}
        </div>
      </section>

      <section className="cycles-stats-grid">
        <article className="cycles-stat-card">
          <span>Total exibido</span>
          <strong>{stats.total}</strong>
          <p>Períodos retornados para os filtros atuais.</p>
        </article>
        <article className="cycles-stat-card">
          <span>Ativos agora</span>
          <strong>{stats.activeCount}</strong>
          <p>Meses marcados como vigentes para lançamentos e feedbacks.</p>
        </article>
        <article className="cycles-stat-card">
          <span>Encerrados</span>
          <strong>{stats.closed}</strong>
          <p>Períodos fechados ou desativados na operação.</p>
        </article>
      </section>

      {showForm && canManage && (
        <section
          ref={formSectionRef}
          className="cycles-surface cycles-form-surface cycles-form-surface--premium"
        >
          <div className="cycles-section-header cycles-form-section-header">
            <div>
              <span className="cycles-section-kicker">Cadastro</span>
              <h2>{editingCycleId ? 'Editar período' : 'Criar novo período'}</h2>
            </div>
            <p>
              {editingCycleId
                ? 'Atualize o nome, o intervalo mensal e o status do período selecionado.'
                : 'Defina o nome, o mês completo de aferição e a data final de fechamento.'}
            </p>
          </div>

          <form className="cycles-form cycles-form--premium" onSubmit={handleSubmit}>
            <div className="cycles-form-grid cycles-form-grid-single">
              <div className="form-group">
                <label htmlFor="cycle-name">Nome do período *</label>
                <input
                  id="cycle-name"
                  className="premium-text-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex.: Janeiro 2026"
                  required
                />
              </div>
            </div>

            <div className="cycles-form-grid cycles-form-grid-dates">
              <div className="form-group">
                <label htmlFor="cycle-start">Data de início *</label>
                <input
                  id="cycle-start"
                  className="premium-text-input"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cycle-end">Data de fim *</label>
                <input
                  id="cycle-end"
                  className="premium-text-input"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="cycle-deadline">Data de fechamento *</label>
                <input
                  id="cycle-deadline"
                  className="premium-text-input"
                  type="date"
                  value={formData.deadlineDate}
                  onChange={(e) => setFormData({ ...formData, deadlineDate: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="cycles-form-grid cycles-form-grid-active-row">
              <div className="cycles-form-active-block">
                <PremiumSelect
                  id="cycle-active"
                  label="Marcar como período ativo"
                  wrapperClassName="premium-select-field"
                  value={formData.active ? 'true' : 'false'}
                  options={cycleActiveFieldOptions}
                  onChange={(value) =>
                    setFormData({ ...formData, active: value === 'true' })
                  }
                />
                <p className="cycles-form-hint">
                  Só um período pode estar em vigor por vez. Ao marcar este como ativo, os demais são
                  encerrados automaticamente ao salvar.
                </p>
              </div>
            </div>

            <div className="cycles-form-footer cycles-form-footer--premium">
              <button
                type="button"
                className="cycles-form-footer-btn cycles-form-footer-btn--ghost"
                onClick={closeForm}
              >
                Cancelar
              </button>
              <button type="submit" className="cycles-form-footer-btn cycles-form-footer-btn--primary">
                {editingCycleId ? 'Salvar alterações' : 'Criar período'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="cycles-surface">
        <div className="cycles-section-header">
          <div>
            <span className="cycles-section-kicker">Histórico</span>
            <h2>Períodos cadastrados</h2>
          </div>
          <p>
            {sortedCycles.length === 0
              ? 'Nenhum período cadastrado ainda.'
              : `${filteredCycles.length} período(s) encontrado(s)`}
          </p>
        </div>

        {sortedCycles.length > 0 && (
          <div className="cycles-toolbar">
            <div className="cycles-filters premium-filters">
              <PremiumSelect
                id="cycles-filter-year"
                label="Ano"
                wrapperClassName="premium-select-field"
                value={selectedYear}
                options={cycleToolbarYearOptions}
                onChange={setSelectedYear}
                placeholder="Todos os anos"
              />

              <PremiumSelect
                id="cycles-filter-period"
                label="Período"
                wrapperClassName="premium-select-field"
                value={selectedPeriod}
                options={cycleToolbarPeriodOptions}
                onChange={setSelectedPeriod}
                placeholder="Todos os períodos"
              />
            </div>

            {hasFilters && (
              <button
                type="button"
                className="premium-filter-reset cycles-filter-reset"
                onClick={() => {
                  setSelectedYear('all');
                  setSelectedPeriod('all');
                }}
              >
                Limpar filtros
              </button>
            )}
          </div>
        )}

        {filteredCycles.length === 0 ? (
          <div className="cycles-empty-block">
            <strong>Nada por aqui</strong>
            <p>
              {sortedCycles.length === 0
                ? canManage
                  ? 'Crie o primeiro período para iniciar as aferições da operação.'
                  : 'Aguarde a liderança configurar os períodos de aferição.'
                : 'Nenhum período corresponde aos filtros selecionados.'}
            </p>
          </div>
        ) : (
          <div className="cycles-grid">
            {filteredCycles.map((cycle) => (
              <article className={`cycle-card${cycle.active ? ' is-active' : ''}`} key={cycle.id}>
                <div className="cycle-card-header">
                  <div className="cycle-avatar" aria-hidden="true">
                    {getInitials(cycle.name)}
                  </div>
                  <div className="cycle-card-headline">
                    <strong>{cycle.name}</strong>
                    <span>Criado em {formatDate(cycle.createdAt)}</span>
                  </div>
                </div>

                <div className="cycle-card-body">
                  <div className="cycle-chip-row">
                    {cycle.active ? (
                      <span className="cycle-status-pill is-active">Em vigor</span>
                    ) : (
                      <span className="cycle-status-pill is-closed">Encerrado</span>
                    )}
                    <span className={`cycle-deadline-pill ${deadlineTone(cycle)}`}>
                      Fecha em {formatDate(cycle.deadlineDate)}
                    </span>
                  </div>

                  <dl className="cycle-meta-grid">
                    <div>
                      <dt>Início</dt>
                      <dd>{formatDate(cycle.startDate)}</dd>
                    </div>
                    <div>
                      <dt>Fim</dt>
                      <dd>{formatDate(cycle.endDate)}</dd>
                    </div>
                    <div>
                      <dt>Ano</dt>
                      <dd>{getCycleYear(cycle)}</dd>
                    </div>
                    <div>
                      <dt>Período</dt>
                      <dd>{getCyclePeriodLabel(getCyclePeriodKey(cycle))}</dd>
                    </div>
                    <div className="cycle-meta-span-2">
                      <dt>Última atualização</dt>
                      <dd>{formatDate(cycle.updatedAt)}</dd>
                    </div>
                  </dl>

                  {canManage && (
                    <div className="cycle-card-actions">
                      <button
                        type="button"
                        className="cycle-card-button"
                        onClick={() => openEditForm(cycle)}
                      >
                        Editar
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          className="cycle-card-button is-danger"
                          onClick={() => handleDelete(cycle)}
                        >
                          Excluir
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
      <ConfirmModal
        isOpen={Boolean(confirmDialog?.isOpen)}
        title={confirmDialog?.title}
        message={confirmDialog?.message || ''}
        confirmLabel="Excluir"
        isLoading={isConfirmingDelete}
        onClose={closeConfirmDialog}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default Cycles;
